import { useEffect, useMemo, useRef, useState } from "react";
import { MapPin, Navigation } from "lucide-react";
import ConciergeMap from "./map/ConciergeMap";
import localization from "../../../../../assets/localization";

interface PlacesTemplateProps {
  template: any;
  language: string;
}

type Place = {
  name: string;
  address: string;
  lat: number;
  lng: number;
};

const parsePlaces = (template: any): Place[] => {
  const points =
    template?.type === "map_point" ? [template] : template?.templates ?? [];

  return points
    .map((point: any) => {
      if (typeof point?.longlan !== "string") return null;
      const [latRaw, lngRaw] = point.longlan.split(",").map((s: string) => s.trim());
      const lat = parseFloat(latRaw);
      const lng = parseFloat(lngRaw);
      if (isNaN(lat) || isNaN(lng)) return null;
      return {
        name: point.name || "",
        address: point.address || "",
        lat,
        lng,
      };
    })
    .filter(Boolean) as Place[];
};

const mapsUrl = (place: Place) =>
  `https://maps.google.com/?q=${place.lat},${place.lng}`;

const directionsUrl = (place: Place) =>
  `https://www.google.com/maps/dir/?api=1&destination=${place.lat},${place.lng}`;

// KB map points (hotel entities with coordinates) on the interactive
// concierge map — every point is a live marker, not one embed at a time.
export default function PlacesTemplate({
  template,
  language,
}: PlacesTemplateProps) {
  const t = localization.get(language);
  const places = useMemo(() => parsePlaces(template), [template]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const railRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    railRef.current
      ?.querySelector(".cmap-card.active")
      ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [selectedIndex]);

  if (!places.length) return null;

  return (
    <div className="cmap-template">
      <ConciergeMap
        hotel={null}
        points={places}
        mode="map"
        selectedIndex={selectedIndex}
        onSelect={setSelectedIndex}
        language={language}
      />

      <div className="cmap-head">
        <span className="cmap-head-title">{t.anchor_places}</span>
        {places.length > 1 && (
          <span className="cmap-head-sub">
            {places.length} {t.places}
          </span>
        )}
      </div>

      <div className="cmap-rail" ref={railRef}>
        {places.map((place, index) => (
          <div
            key={index}
            className={`cmap-card ${index === selectedIndex ? "active" : ""}`}
            onClick={() => setSelectedIndex(index)}
          >
            <div className="cmap-card-head">
              <span className="cmap-card-chip place">
                <MapPin className="icon" />
              </span>
              <span className="cmap-card-name">
                {place.name || `${place.lat.toFixed(4)}, ${place.lng.toFixed(4)}`}
              </span>
            </div>
            {place.address && (
              <div className="cmap-card-meta">{place.address}</div>
            )}
            <div className="cmap-card-actions">
              <a
                href={mapsUrl(place)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {t.map_directions}
              </a>
              <a
                className="secondary"
                href={directionsUrl(place)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <Navigation className="icon" />
                {t.directions}
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
